import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { BASE_URL } from '../components/constants';

// Async thunk for checking task usage
export const checkTaskUsage = createAsyncThunk(
  'usage/checkTask',
  async ({ organization_id, task_id }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return rejectWithValue({ error: 'No authentication token found' });
      }

      const response = await fetch(`${BASE_URL}/usage/check-task`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ organization_id, task_id }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data);
      }
      
      return data;
    } catch (error) {
      return rejectWithValue({ error: '"Die für diese Aufgabe festgelegten Limits sind erreicht. Bitte wenden Sie sich an Ihren Administrator, um ein Abonnement mit höheren Limits einzurichten.."' });
    }
  }
);

// Async thunk for checking page usage
export const checkPageUsage = createAsyncThunk(
  'usage/checkPages',
  async ({ organization_id, history_id, total_pages, task_id }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return rejectWithValue({ error: 'No authentication token found' });
      }

      console.log('[usageSlice][checkPageUsage] request:', { organization_id, history_id, total_pages, task_id });
      // Use authoritative check endpoint that validates against available_pages and returns remaining
      const response = await fetch(`${BASE_URL}/usage/check-pages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ organization_id, history_id, total_pages, task_id }),
      });
      
      const data = await response.json();
      console.log('[usageSlice][checkPageUsage] response ok?', response.ok, 'data:', data);
      if (!response.ok) {
        return rejectWithValue(data);
      }
      return data; // { can_proceed, remaining_pages, max_pages }
    } catch (error) {
      console.log('[usageSlice][checkPageUsage] exception:', error);
      return rejectWithValue({ error: 'Failed to check page usage' });
    }
  }
);

// Async thunk for recording task usage
export const recordTaskUsage = createAsyncThunk(
  'usage/recordTask',
  async ({ organization_id, task_id }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${BASE_URL}/usage/record-task`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ organization_id, task_id }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data);
      }
      
      return data;
    } catch (error) {
      return rejectWithValue({ error: 'Failed to record task usage' });
    }
  }
);

// Async thunk for recording page usage
export const recordPageUsage = createAsyncThunk(
  'usage/recordPages',
  async ({ organization_id, history_id, total_pages, task_id }, { rejectWithValue }) => {
    try {
      const response = await fetch(`${BASE_URL}/usage/record-pages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ organization_id, history_id, total_pages, task_id }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        return rejectWithValue(data);
      }
      
      return data;
    } catch (error) {
      return rejectWithValue({ error: 'Failed to record page usage' });
    }
  }
);

const usageSlice = createSlice({
  name: 'usage',
  initialState: {
    loading: false,
    error: null,
    lastTaskCheck: null,
    lastPageCheck: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearLastChecks: (state) => {
      state.lastTaskCheck = null;
      state.lastPageCheck = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Check task usage
      .addCase(checkTaskUsage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkTaskUsage.fulfilled, (state, action) => {
        state.loading = false;
        state.lastTaskCheck = action.payload;
      })
      .addCase(checkTaskUsage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Check page usage
      .addCase(checkPageUsage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkPageUsage.fulfilled, (state, action) => {
        state.loading = false;
        state.lastPageCheck = action.payload;
      })
      .addCase(checkPageUsage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Record task usage
      .addCase(recordTaskUsage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(recordTaskUsage.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(recordTaskUsage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Record page usage
      .addCase(recordPageUsage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(recordPageUsage.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(recordPageUsage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearLastChecks } = usageSlice.actions;
export default usageSlice.reducer;
